import { EvchargeStrategy } from "ngx-core-services";

export interface EvchargeStrategyDetails {
  label: string;
  code: EvchargeStrategy;
  src: string;
  variation?: Partial<EvchargeStrategyDetails>;
  isEnabled: (code?: EvchargeStrategy) => boolean;
  description: string;
};

export const getEnumKeyName = <T extends object>(enumObj: T, value: number): keyof T | undefined => {
  return Object.keys(enumObj).find((key) => enumObj[key as keyof T] === value) as keyof T | undefined;
};

export const getLabels = () => ({
  charging: 'Charging',
  duration: 'Duration',
  error: 'Error',
  fast: 'Fast',
  fastDescription: 'For fast, easy and safe charging. recharge quickly and travel as much as you want.',
  firmwareVer: 'Firmware Ver.',
  idle: 'Idle',
  disconnected: 'Disconnected',
  green: 'Green',
  greenDescription: 'For ecological, clean and efficient charging. your responsible charging.',
  greenMixed: 'Green Mixed',
  kilometerDescription: 'For smart charging, recharge only the energy you need.',
  localeCode: 'en-EN',
  maxPower: 'Max Power',
  model: 'Model',
  parking: 'Parking',
  singleKilometer: 'Single Kilometer',
  singleTimePower: 'Single Time Power',
  startRecharge: 'Start Recharge',
  status: 'Status',
  strategy: 'Strategy',
  timePowerDescription: 'For a scheduled recharge, you decide when to recharge.',
  title: 'Wallbox',
  unknown: 'Unknown',
  zeroEmission: 'Zero Emission',
  zeroEmissionDescription: 'For ecological, clean and efficient charging. your responsible charging.',
});

export const getStrategies = (_labels: Partial<ReturnType<typeof getLabels>>): EvchargeStrategyDetails[] => [
  {
    label: _labels.fast || getLabels().fast,
    src: 'blocks-assets/images/evcharge-strategies/fast.svg',
    isEnabled: (code) => code === EvchargeStrategy.Fast,
    code: EvchargeStrategy.Fast,
    description: _labels.fastDescription || getLabels().fastDescription,
  },
  {
    label: _labels.green || getLabels().green,
    src: 'blocks-assets/images/evcharge-strategies/green.svg',
    isEnabled: (code) => [EvchargeStrategy.Green, EvchargeStrategy.Green_mixed].includes(code || 999 as EvchargeStrategy),
    variation: {
      label: _labels.greenMixed || getLabels().greenMixed,
      code: EvchargeStrategy.Green_mixed,
    },
    code: EvchargeStrategy.Green,
    description: _labels.greenDescription || getLabels().greenDescription,
  },
  {
    label: _labels.singleKilometer || getLabels().singleKilometer,
    src: 'blocks-assets/images/evcharge-strategies/km.svg',
    isEnabled: (code) => [EvchargeStrategy.Km_repeat, EvchargeStrategy.Km_single].includes(code || 999 as EvchargeStrategy),
    variation: {
      label: _labels.singleKilometer || getLabels().singleKilometer,
      code: EvchargeStrategy.Km_repeat,
    },
    code: EvchargeStrategy.Km_single,
    description: _labels.kilometerDescription || getLabels().kilometerDescription,
  },
  {
    label: _labels.singleTimePower || getLabels().singleTimePower,
    src: 'blocks-assets/images/evcharge-strategies/time-power.svg',
    isEnabled: (code) => [EvchargeStrategy.Time_power, EvchargeStrategy.Time_power_single].includes(code || 999 as EvchargeStrategy),
    variation: {
      label: _labels.singleTimePower || getLabels().singleTimePower,
      code: EvchargeStrategy.Time_power,
    },
    code: EvchargeStrategy.Time_power_single,
    description: _labels.timePowerDescription || getLabels().timePowerDescription,
  },
  {
    label: _labels.zeroEmission || getLabels().zeroEmission,
    src: 'blocks-assets/images/evcharge-strategies/zero-emissions.svg',
    isEnabled: (code) => code === EvchargeStrategy.Zero_emissions,
    code: EvchargeStrategy.Zero_emissions,
    description: _labels.zeroEmissionDescription || getLabels().zeroEmissionDescription,
  },
];

export const getStatusDetails = (
  statusKey: string | undefined,
  labels: ReturnType<typeof getLabels>
): { label: string; cssClasses: string } => {
  console.log('getStatusDetails', statusKey, labels);
  switch (statusKey) {
    case 'Charging':
      return {
        label: labels.charging,
        cssClasses: 'bg-green-100 border-green-500 text-green-500',
      };
    case 'Error':
    case 'Disconnected':
      return {
        label: labels.disconnected,
        cssClasses: 'bg-red-100 border-red-400 text-red-400',
      };
    case 'Idle':
      return {
        label: labels.idle,
        cssClasses: 'bg-accent-100 border-accent-500 text-accent-500',
      };
    case 'Parking':
      return {
        label: labels.parking,
        cssClasses: 'bg-blue-100 border-blue-400 text-blue-400',
      };
    case 'Start':
      return {
        label: labels.startRecharge,
        cssClasses: 'bg-grey-100 border-grey-400 text-grey-400',
      };
    default:
      return {
        label: labels.unknown,
        cssClasses: 'bg-grey-100 border-grey-300 text-grey-300',
      };
  }
};
